import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { formatDate } from '../../../../utils/Utils';

// Mesmo padrão visual de CronStatusChip/SubscriptionStatusBadge (bolinha + texto).
const STATUS_CONFIG = {
  connected: { label: 'Conectada', bg: '#E8F5E9', border: '#C8E6C9', dot: '#2E7D32', text: '#2E7D32' },
  disconnected: { label: 'Desconectada', bg: '#FFEBEE', border: '#FFCDD2', dot: '#E53935', text: '#C62828' },
  error: { label: 'Erro', bg: '#FFEBEE', border: '#FFCDD2', dot: '#E53935', text: '#C62828' },
  not_configured: { label: 'Não configurada', bg: '#FFF3E0', border: '#FFE0B2', dot: '#FB8C00', text: '#E65100' },
  awaiting_connection: { label: 'Aguardando conexão', bg: '#FFF3E0', border: '#FFE0B2', dot: '#FB8C00', text: '#E65100' },
};
const UNAVAILABLE_STATUS = { label: 'Sem checagem ainda', bg: '#F5F5F5', border: '#E0E0E0', dot: '#9E9E9E', text: '#616161' };

/**
 * Formata uma data 'Y-m-d H:i:s' (fuso do servidor) usando formatDate() do projeto.
 * @param {string|null} dateStr
 * @returns {string|null}
 */
function fmtDate(dateStr) {
  if (!dateStr) return null;
  const date = new Date(dateStr.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return null;
  return formatDate(date);
}

/**
 * WhatsAppPlatformStatusCard - Status da instância compartilhada do WhatsApp da plataforma
 * (Admin Geral > WhatsApp)
 * @param {Object} props
 * @param {Object} props.overview - retorno de overview.php
 */
function WhatsAppPlatformStatusCard({ overview }) {
  const instance = overview?.platform_instance || {};
  const cfg = !instance.available
    ? UNAVAILABLE_STATUS
    : (STATUS_CONFIG[instance.last_status] || UNAVAILABLE_STATUS);
  const checkedAt = fmtDate(instance.last_status_checked_at);

  return (
    <Card
      sx={{
        borderRadius: '12px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        border: '1px solid #E8ECF0',
        p: '20px',
        mb: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              bgcolor: '#E8F5E9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <WhatsAppIcon sx={{ color: '#2E7D32', fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Número da plataforma</Typography>
            <Typography variant="body2" color="text.secondary">
              {checkedAt ? `Última checagem: ${checkedAt}` : 'Última checagem: —'}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.6,
            px: 1.25,
            py: 0.4,
            borderRadius: '20px',
            backgroundColor: cfg.bg,
            border: `1px solid ${cfg.border}`,
            width: 'fit-content',
          }}
        >
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: cfg.dot, flexShrink: 0 }} />
          <Typography
            variant="caption"
            sx={{ fontWeight: 500, fontSize: '0.75rem', lineHeight: 1, color: cfg.text, whiteSpace: 'nowrap' }}
          >
            {cfg.label}
          </Typography>
        </Box>
      </Box>

      {instance.last_status_error && (
        <Typography variant="body2" color="error.main" sx={{ fontSize: '0.8125rem' }}>
          {instance.last_status_error}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          Modo da política: <strong>{overview?.policy_mode || '—'}</strong>
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Número da plataforma: <strong>{overview?.platform_enabled ? 'ativo' : 'desativado'}</strong>
        </Typography>
      </Box>
    </Card>
  );
}

export default WhatsAppPlatformStatusCard;
